import apiClient, { ApiMessage } from "./client";

/** 资料访问明细 */
export interface MaterialActivity {
  materialid: string;
  title: string;
  type: string;
  ppttype?: string;
  primarycategory?: string;
  category?: string;
  modelcategory?: string;
  filesize?: string;
  thumbnail?: string;
  isvideo?: number;
  videoduration?: string;
  fileurl?: string;
  description?: string;
  tags?: string;
  confidential?: number;
  confidentialagents?: string;
  /** 是否允许下载 1=允许 */
  downloadallowed?: number;
  views: number;
  downloads: number;
  lastaccesstime: number;
}

/**
 * 获取当前登录用户信息（library 页面专用）
 * POST /agentfront/getCurrentUser.htm
 */
export async function getCurrentUser(): Promise<CurrentUser> {
  const response = await apiClient.post<ApiMessage & { data?: CurrentUser }>(
    "/agentfront/getCurrentUser.htm"
  );

  if (!response.data.success) {
    throw new Error(response.data.info || "获取用户信息失败");
  }

  return response.data.data!;
}

/** 当前用户信息（PortalNav 专用） */
export interface CurrentUser {
  id: string;
  name: string;
  loginname: string;
  frontflag: boolean;
  accountType: string;
  email?: string;
  company?: string;
  region?: string;
  expirydate?: number;
  headimg?: string;
  state?: number;
}

/** 用户基本资料 */
export interface UserInfo {
  id: string;
  name: string;
  loginname: string;
  state: number;
  frontflag: boolean;
  createdate: number;
  lastlogindate: number;
  company?: string;
  region?: string;
  expirydate?: number;
  notes?: string;
  mobile?: string;
  email?: string;
  headimg?: string;
  totalviews?: number;
  totaldownloads?: number;
  receiveEmail?: boolean;
  modelIds?: string[];
  materialactivities?: MaterialActivity[];
}

/** 创建代理商请求参数 */
export interface CreateAgentParams {
  name: string;
  email: string;
  company: string;
  region: string;
  password: string;
  expiryDays: string;
  notes?: string;
  modelIds?: string;
}

/** 创建代理商返回结果 */
export interface CreateAgentResult {
  id: string;
  name: string;
  loginname: string;
  email: string;
  company?: string;
  region?: string;
  expirydate?: number;
  notes?: string;
}

/**
 * 创建代理商账号
 * POST /agentinfo/createAgent.do
 */
export async function createAgent(params: CreateAgentParams): Promise<CreateAgentResult> {
  const formData = new URLSearchParams();
  formData.append("name", params.name);
  formData.append("email", params.email);
  formData.append("company", params.company);
  formData.append("region", params.region);
  formData.append("password", params.password);
  formData.append("expiryDays", params.expiryDays);
  if (params.notes) {
    formData.append("notes", params.notes);
  }
  if (params.modelIds) {
    formData.append("modelIds", params.modelIds);
  }

  const response = await apiClient.post<ApiMessage & { data?: CreateAgentResult }>(
    "/agentinfo/createAgent.do",
    formData.toString()
  );

  if (!response.data.success) {
    throw new Error(response.data.info || "创建代理商失败");
  }

  return response.data.data!;
}

/** 查询代理商列表请求参数 */
export interface ListAgentParams {
  pageNumber?: number;
  pageSize?: number;
  keyword?: string;
  state?: number;
  region?: string;
}

/** 查询代理商列表返回结果 */
export interface ListAgentResult {
  list: UserInfo[];
  total: number;
  pageNumber: number;
  pageSize: number;
}

/**
 * 分页查询代理商列表
 * POST /agentinfo/listAgent.do
 */
export async function listAgent(params: ListAgentParams): Promise<ListAgentResult> {
  const formData = new URLSearchParams();
  if (params.pageNumber !== undefined) formData.append("pageNumber", String(params.pageNumber));
  if (params.pageSize !== undefined) formData.append("pageSize", String(params.pageSize));
  if (params.keyword) formData.append("keyword", params.keyword);
  if (params.state !== undefined && params.state !== null) formData.append("state", String(params.state));
  if (params.region) formData.append("region", params.region);

  const response = await apiClient.post<ApiMessage & { data?: ListAgentResult }>(
    "/agentinfo/listAgent.do",
    formData.toString()
  );

  if (!response.data.success) {
    throw new Error(response.data.info || "获取代理商列表失败");
  }

  return response.data.data!;
}

/**
 * 更新代理商备注
 * POST /agentinfo/updateAgentNotes.do
 */
export async function updateAgentNotes(id: string, notes: string): Promise<void> {
  const formData = new URLSearchParams();
  formData.append("id", id);
  formData.append("notes", notes);

  const response = await apiClient.post<ApiMessage>(
    "/agentinfo/updateAgentNotes.do",
    formData.toString()
  );

  if (!response.data.success) {
    throw new Error(response.data.info || "更新备注失败");
  }
}

/**
 * 重置代理商密码
 * POST /sysuser/resetPassword.do
 */
export async function resetAgentPassword(userid: string, password: string): Promise<void> {
  const formData = new URLSearchParams();
  formData.append("userid", userid);
  formData.append("password", password);

  const response = await apiClient.post<ApiMessage>(
    "/sysuser/resetPassword.do",
    formData.toString()
  );

  if (!response.data.success) {
    throw new Error(response.data.info || "重置密码失败");
  }
}

/**
 * 启用/禁用代理商
 * POST /agentinfo/changeAgentStatus.do
 * @param id 代理商ID
 * @param state 0=启用, 2=禁用
 */
export async function changeAgentStatus(id: string, state: 0 | 2): Promise<void> {
  const formData = new URLSearchParams();
  formData.append("id", id);
  formData.append("state", String(state));

  const response = await apiClient.post<ApiMessage>(
    "/agentinfo/changeAgentStatus.do",
    formData.toString()
  );

  if (!response.data.success) {
    throw new Error(response.data.info || "状态变更失败");
  }
}

/**
 * 开启/关闭代理商邮件通知
 * POST /agentinfo/changeAgentEmailNotify.do
 * @param id 代理商ID
 * @param receiveEmail true=接收邮件, false=不接收
 */
export async function changeAgentEmailNotify(id: string, receiveEmail: boolean): Promise<void> {
  const formData = new URLSearchParams();
  formData.append("id", id);
  formData.append("receiveEmail", receiveEmail ? "1" : "0");

  const response = await apiClient.post<ApiMessage>(
    "/agentinfo/changeAgentEmailNotify.do",
    formData.toString()
  );

  if (!response.data.success) {
    throw new Error(response.data.info || "邮件通知设置失败");
  }
}

/**
 * 获取单个代理商详情
 * POST /agentinfo/getAgent.do
 */
export async function getAgent(id: string): Promise<UserInfo> {
  const formData = new URLSearchParams();
  formData.append("id", id);

  const response = await apiClient.post<ApiMessage & { data?: UserInfo }>(
    "/agentinfo/getAgent.do",
    formData.toString()
  );

  if (!response.data.success) {
    throw new Error(response.data.info || "获取代理商详情失败");
  }

  return response.data.data!;
}

/** 更新代理商请求参数 */
export interface UpdateAgentParams {
  id: string;
  name?: string;
  state?: number;
  company?: string;
  region?: string;
  expirydate?: number;
  notes?: string;
  modelIds?: string;
}

/**
 * 更新代理商信息
 * POST /agentinfo/updateAgent.do
 */
export async function updateAgent(params: UpdateAgentParams): Promise<void> {
  const formData = new URLSearchParams();
  formData.append("id", params.id);
  if (params.name) formData.append("name", params.name);
  if (params.state !== undefined) formData.append("state", String(params.state));
  if (params.company) formData.append("company", params.company);
  if (params.region) formData.append("region", params.region);
  if (params.expirydate !== undefined) formData.append("expirydate", String(params.expirydate));
  if (params.notes !== undefined) formData.append("notes", params.notes);
  if (params.modelIds !== undefined) formData.append("modelIds", params.modelIds);

  const response = await apiClient.post<ApiMessage>(
    "/agentinfo/updateAgent.do",
    formData.toString()
  );

  if (!response.data.success) {
    throw new Error(response.data.info || "更新代理商失败");
  }
}

/**
 * 获取当前登录用户基本资料
 * GET/POST /agentinfo/getUserInfo.do
 */
export async function getUserInfo(): Promise<UserInfo> {
  const response = await apiClient.post<ApiMessage & { data?: UserInfo }>(
    "/agentinfo/getUserInfo.do"
  );

  if (!response.data.success) {
    throw new Error(response.data.info || "获取用户信息失败");
  }

  return response.data.data!;
}
